import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Colors } from "../../utils/colors";
import { FoldHeaderText } from "./SubComponents/FoldHeaderText";
import { MARKITAI } from "@markit/common.utils";
import {
  OldDominionCarousel,
  StoopDatingCarousel,
  ComedyStoreCarousel,
} from "../../assets/LandingPage/Carousel/TopCarousel/topCarouselIndex";
import {
  NicoleSalibaCarousel,
  ClaireSawaCarousel,
  HorchataSotoCarousel,
} from "../../assets/LandingPage/Carousel/BottomCarousel/bottomCarouselIndex";
import OldDirtyBoston from "../../assets/LandingPage/OtherCreators/OldDirtyBoston.png";
import BloomMusic from "../../assets/LandingPage/OtherCreators/BloomMusic.png";
import TheJoyKindler from "../../assets/LandingPage/OtherCreators/TheJoyKindler.png";

type TestimonialsFoldProps = {
  isDesktopSize: boolean;
};

type TestimonialItem = {
  photo: string;
  content: ReactNode;
  name: string;
  label: string;
  username: string;
};

const TESTIMONIAL_ITEMS: TestimonialItem[] = [
  {
    photo: BloomMusic,
    content: (
      <span>
        Markit engages your audience beyond social media. It{" "}
        <span style={{ color: Colors.BLUE5 }}>goes directly to your phone</span>
        , instead of on social media where you&apos;re not really guaranteed
        that your followers will even see your posts anymore with the new
        algorithm.
      </span>
    ),
    name: "Isabella Erardi",
    label: "Artist, Bloom Music",
    username: "bloommusic",
  },
  {
    photo: ComedyStoreCarousel,
    content: (
      <span>
        The ROI is a no-brainer. We&apos;ll spend about{" "}
        <span style={{ color: Colors.BLUE5 }}>
          $100 on a text blast and pull in $2,000+ in ticket and drink sales
        </span>
        , and the revenue tracking Markit built for us has been huge for proving
        that to the rest of the team.
      </span>
    ),
    name: "Mike",
    label: "Manager, The Comedy Store",
    username: "thecomedystore",
  },
  {
    photo: ClaireSawaCarousel,
    content: (
      <span>
        My favorite creator tool right now is Markit&apos;s Instagram
        automations. I&apos;ll always promote Markit for free, that&apos;s how
        much I love it. I even get help directly from their CEO — they really
        care about their creators.
      </span>
    ),
    name: "Claire Sawa",
    label: "Jelly Art Creator",
    username: "clairesawa",
  },
  {
    photo: NicoleSalibaCarousel,
    content: (
      <span>
        I&apos;ve been using Markit for about 3 months now for my event business
        and I absolutely love it. It is the best, I love that I&apos;m able to
        text people so easily.{" "}
        <span style={{ color: Colors.BLUE5 }}>
          Literally people text me all the time
        </span>{" "}
        on my app too so I love it so much.
      </span>
    ),
    name: "Nicole Saliba",
    label: "Founder, Let's Get Dressed Boston",
    username: "letsgetdressed",
  },
  {
    photo: TheJoyKindler,
    content: (
      <span>
        What an incredible opportunity to have that{" "}
        <span style={{ color: Colors.BLUE5 }}>connection with my audience</span>{" "}
        - in an immediate way … that is unique in its delivery and that
        doesn&apos;t require an app to download, a community to join; no emails,
        no sales pitch.
      </span>
    ),
    name: "Vicki O'Grady-Longo",
    label: "Founder, The Joy Kindler",
    username: "thejoykindler",
  },
  {
    photo: StoopDatingCarousel,
    content: (
      <span>
        Markit changed the whole trajectory of my business — it&apos;s the
        reason I could quit my job.{" "}
        <span style={{ color: Colors.BLUE5 }}>
          Texting my 900+ subscribers directly instead of DMing
        </span>{" "}
        one by one has been a game changer for my events.
      </span>
    ),
    name: "Julz Radley",
    label: "Founder, Stoop Dating",
    username: "stoopdating",
  },
  {
    photo: OldDominionCarousel,
    content: (
      <span>
        Markit is a game-changer — we text our fans, and{" "}
        <span style={{ color: Colors.BLUE5 }}>they actually text back</span>. It
        clicked when I saw &quot;I named my daughter after this song.&quot;
      </span>
    ),
    name: "Alexa",
    label: "Manager, Old Dominion",
    username: "olddominion",
  },
  {
    photo: HorchataSotoCarousel,
    content: (
      <span>
        My favorite thing about Markit is the automations — you comment or send
        a message, and it automatically follows up for you. It&apos;s such an{" "}
        <span style={{ color: Colors.BLUE5 }}>
          easy way to promote a product
        </span>{" "}
        or a link. I think it&apos;s the coolest thing.
      </span>
    ),
    name: "Jorge Soto",
    label: "Creator, Horchata Soto",
    username: "horchatasoto",
  },
  {
    photo: OldDirtyBoston,
    content: (
      <span>
        It&apos;s a great little community building tool, and I know that email
        really has not been that effective for me, and I think that{" "}
        <span style={{ color: Colors.BLUE5 }}>
          text is like a way more direct and personal
        </span>{" "}
        way to connect.
      </span>
    ),
    name: "Jason Faulkner",
    label: "Founder, Old Dirty Boston",
    username: "olddirtyboston",
  },
];

// Matches CarouselContainer's auto-advance interval
const AUTO_ADVANCE_MS = 5000;

const TestimonialsFold = (props: TestimonialsFoldProps) => {
  const { isDesktopSize } = props;
  const [index, setIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const foldRef = useRef<HTMLDivElement>(null);

  const goTo = useCallback((next: number) => {
    setIndex(next);
    setFadeKey((key) => key + 1);
  }, []);

  // Don't start auto-advancing until the fold has actually scrolled into view.
  useEffect(() => {
    const el = foldRef.current;
    if (!el || hasEnteredView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredView(true);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasEnteredView]);

  useEffect(() => {
    if (isHovering || !hasEnteredView) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % TESTIMONIAL_ITEMS.length);
      setFadeKey((key) => key + 1);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [isHovering, hasEnteredView]);

  const item = TESTIMONIAL_ITEMS[index];

  const avatarTabs = useMemo(
    () => (
      <div
        className="AlignedRow HideScrollbar"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{
          gap: 10,
          padding: 6,
          borderRadius: 100,
          border: `1px solid ${Colors.BLACK1}`,
          backgroundColor: Colors.BLACK,
          width: "fit-content",
          maxWidth: "100%",
          overflowX: "auto",
          boxSizing: "border-box",
        }}
      >
        {TESTIMONIAL_ITEMS.map((testimonial, i) => (
          <img
            key={testimonial.username}
            src={testimonial.photo}
            alt={testimonial.name}
            onClick={() => goTo(i)}
            style={{
              width: 43,
              height: 43,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              cursor: "pointer",
              border: i === index ? `2px solid ${Colors.WHITE}` : "none",
              opacity: i === index ? 1 : 0.6,
              transition: isDesktopSize
                ? "opacity 0.2s ease-out, border-color 0.2s ease-out"
                : undefined,
            }}
          />
        ))}
      </div>
    ),
    [goTo, index, isDesktopSize]
  );

  return (
    <div
      ref={foldRef}
      className="ColumnCentering"
      style={{ gap: isDesktopSize ? 60 : 40, width: "100%" }}
    >
      <FoldHeaderText
        isDesktopSize={isDesktopSize}
        textStyles={{
          textAlign: isDesktopSize ? "center" : "left",
        }}
      >
        What our customers are saying
      </FoldHeaderText>
      <div
        className={isDesktopSize ? "AlignedRow" : "ColumnNormal"}
        style={{
          gap: isDesktopSize ? 60 : 24,
          width: "100%",
          maxWidth: isDesktopSize ? 1152 : 480,
          alignItems: isDesktopSize ? "center" : "stretch",
        }}
      >
        {!isDesktopSize ? avatarTabs : null}
        <img
          key={fadeKey}
          src={item.photo}
          alt={item.name}
          className="fade-in"
          style={{
            width: "100%",
            maxWidth: isDesktopSize ? 546 : undefined,
            height: isDesktopSize ? 600 : 385,
            borderRadius: 20,
            backgroundColor: Colors.GRAY10,
            objectFit: "cover",
            objectPosition: "center",
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        />
        <div
          className="ColumnNormal"
          style={{
            gap: isDesktopSize ? 30 : 24,
            flex: 1,
            minWidth: 0,
            justifyContent: "flex-start",
            boxSizing: "border-box",
          }}
        >
          {isDesktopSize ? avatarTabs : null}
          <div
            className="ColumnNormal"
            style={{
              gap: isDesktopSize ? 30 : 24,
              minHeight: isDesktopSize ? 260 : undefined,
              width: isDesktopSize ? 540 : "100%",
            }}
          >
            <p
              key={"quote-" + fadeKey}
              className="fade-in"
              style={{
                margin: 0,
                fontSize: isDesktopSize ? 24 : 18,
                lineHeight: isDesktopSize ? "32px" : "26px",
                color: Colors.WHITE,
              }}
            >
              &ldquo;{item.content}&rdquo;
            </p>
            <a
              href={`${MARKITAI}/u/${item.username}`}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <div className="ColumnNormal" style={{ gap: 2 }}>
                <span
                  style={{ color: Colors.WHITE, fontWeight: 600, fontSize: 16 }}
                >
                  {item.name}
                </span>
                <span style={{ color: Colors.GRAY2, fontSize: 14 }}>
                  {item.label}
                </span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(TestimonialsFold);
